/**
 * ComposeMessageHistory Component
 * Dropdown showing recent drafts and sent messages with recovery options
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { useLocale } from '@/contexts/LocaleContext';

import { TYPOGRAPHY, SPACING } from '@/design-system';
import { LComponents, LCommon } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { FileText, Send, ChevronDown, Clock, Trash2, Reply } from 'lucide-react';
import type { MessageDraft } from '@/hooks/useMessageDrafts';

export interface SentMessage {
  id: string;
  subject: string | null;
  message: string | null;
  created_at: string;
  recipient_ids: string[];
  status: string;
}

interface ComposeMessageHistoryProps {
  drafts: MessageDraft[];
  draftsLoading: boolean;
  onLoadDraft: (draft: MessageDraft) => void;
  onDeleteDraft: (draftId: string) => void;
  onLoadSent?: (sentMessage: SentMessage) => void;
}

export function ComposeMessageHistory({
  drafts,
  draftsLoading,
  onLoadDraft,
  onDeleteDraft,
  onLoadSent,
}: ComposeMessageHistoryProps) {
  const { user } = useAuth();
  const { lComponents } = useLocalizations();
  const { formatRelativeTime } = useLocale();
  const [isOpen, setIsOpen] = useState(false);

  // Fetch recent sent messages (last 30 days)
  const { data: sentMessages = [], isLoading: sentLoading } = useQuery({
    queryKey: ['sent-messages', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const data = await db.fetchTop<SentMessage>(
        'opportunities',
        'id, subject, message, created_at, recipient_ids, status',
        10,
        {
          filters: [
            { column: 'user_id', operator: 'eq', value: user.id },
            { column: 'created_at', operator: 'gte', value: thirtyDaysAgo.toISOString() },
          ],
          orderBy: { column: 'created_at', ascending: false },
        }
      );

      return data || [];
    },
    enabled: !!user?.id && isOpen,
    staleTime: 30000,
  });

  const draftCount = drafts.length;
  const sentCount = sentMessages.length;
  const hasHistory = draftCount > 0 || sentCount > 0;

  if (!hasHistory && !draftsLoading && !sentLoading) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 h-7 px-2">
          {draftCount > 0 && (
            <Badge variant="secondary" className="h-5 px-2 text-xs gap-1">
              <FileText className="h-3 w-3" />
              <LComponents id="compose_message.history.drafts_badge" defaultValue="Drafts" />
              <span>({draftCount})</span>
            </Badge>
          )}
          {sentCount > 0 && (
            <Badge variant="outline" className="h-5 px-2 text-xs gap-1">
              <Send className="h-3 w-3" />
              <LComponents id="compose_message.history.sent_badge" defaultValue="Sent" />
              <span>({sentCount})</span>
            </Badge>
          )}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 z-[200]" align="end">
        <ScrollArea className="max-h-80">
          {/* Drafts Section */}
          {draftCount > 0 && (
            <div className={SPACING.stackSm}>
              <div className="flex items-center gap-2 px-3 pt-3 pb-1">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className={cn(TYPOGRAPHY.caption, 'font-medium')}>
                  <LComponents id="compose_message.history.drafts" defaultValue="Drafts" />
                </span>
              </div>
              {drafts.slice(0, 5).map((draft) => (
                <div
                  key={draft.id}
                  className="flex items-center justify-between px-3 py-2 hover:bg-muted/50 cursor-pointer group"
                  onClick={() => {
                    onLoadDraft(draft);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {draft.subject || draft.draft_name || (
                        <LComponents id="compose_message.history.untitled_draft" defaultValue="Untitled Draft" />
                      )}
                    </p>
                    <p className={cn(TYPOGRAPHY.caption, 'flex items-center gap-1')}>
                      <Clock className="h-3 w-3" />
                      {formatRelativeTime(draft.updated_at)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteDraft(draft.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {draftCount > 0 && sentCount > 0 && <Separator className="my-2" />}

          {/* Sent Messages Section */}
          {sentCount > 0 && (
            <div className={SPACING.stackSm}>
              <div className="flex items-center gap-2 px-3 pt-3 pb-1">
                <Send className="h-4 w-4 text-muted-foreground" />
                <span className={cn(TYPOGRAPHY.caption, 'font-medium')}>
                  <LComponents id="compose_message.history.sent" defaultValue="Recently Sent" />
                </span>
              </div>
              {sentMessages.slice(0, 5).map((msg) => (
                <div
                  key={msg.id}
                  className="px-3 py-2 hover:bg-muted/50 cursor-pointer group"
                  onClick={() => {
                    if (onLoadSent) {
                      onLoadSent(msg);
                      setIsOpen(false);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {msg.subject || (
                          <LComponents id="compose_message.history.no_subject" defaultValue="(No Subject)" />
                        )}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className={cn(TYPOGRAPHY.caption, 'flex items-center gap-1')}>
                          <Clock className="h-3 w-3" />
                          {formatRelativeTime(msg.created_at)}
                        </p>
                        <Badge variant="outline" className="h-4 text-[10px] capitalize">
                          {msg.status}
                        </Badge>
                      </div>
                    </div>
                    {onLoadSent && (
                      <Reply className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!hasHistory && !draftsLoading && !sentLoading && (
            <div className="p-4 text-center">
              <p className={TYPOGRAPHY.caption}>
                <LComponents id="compose_message.history.empty" defaultValue='No drafts or sent messages found.' />
              </p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
